/* Photo measuring: a sheet of printer paper taped to the glass is the ruler.
   The visitor drags four points to the paper corners and four to the glass corners. The glass outline (long edges)
   gives the perspective and the paper gives the scale; one least-squares fit of all eight points (glass and paper are
   both true rectangles on the same plane) then gives the glass width and height, with an error band.
   Owner 30/10: the old paper-only homography missed 90 × 127 cm by 10–22 cm on one photo (the small paper's
   perspective, stretched over a window five times larger); a 2000-photo simulation put it at ~15 cm median vs ~3 cm now.
   Runs entirely on the device. Nothing is uploaded. */
(() => {
  'use strict';
  const P = document.currentScript?.dataset.classPrefix || '';
  const PAPER_MM = { letter: [215.9, 279.4], a4: [210, 297] };
  const PAPER_NAME = { letter: 'US Letter', a4: 'A4' };

  /* ---------- Maths (exported for tests) ---------- */
  function solve(A, b) {
    const n = b.length, M = A.map((row, i) => [...row, b[i]]);
    for (let c = 0; c < n; c++) {
      let p = c;
      for (let r = c + 1; r < n; r++) if (Math.abs(M[r][c]) > Math.abs(M[p][c])) p = r;
      if (Math.abs(M[p][c]) < 1e-14) return null;
      [M[c], M[p]] = [M[p], M[c]];
      for (let r = 0; r < n; r++) {
        if (r === c) continue;
        const f = M[r][c] / M[c][c];
        for (let k = c; k <= n; k++) M[r][k] -= f * M[c][k];
      }
    }
    return M.map((row, i) => row[n] / row[i]);
  }
  /** src → dst from 4 point pairs (exact) or more (least squares); h33 = 1 */
  function homography(src, dst) {
    const A = [], b = [];
    for (let i = 0; i < src.length; i++) {
      const [x, y] = src[i], [u, v] = dst[i];
      A.push([x, y, 1, 0, 0, 0, -x * u, -y * u]); b.push(u);
      A.push([0, 0, 0, x, y, 1, -x * v, -y * v]); b.push(v);
    }
    const h = src.length === 4 ? solve(A, b) : solve(
      A[0].map((_, i) => A[0].map((_, j) => A.reduce((s, r) => s + r[i] * r[j], 0))),
      A[0].map((_, i) => A.reduce((s, r, k) => s + r[i] * b[k], 0)));
    return h ? [...h, 1] : null;
  }
  function apply(h, [x, y]) {
    const w = h[6] * x + h[7] * y + h[8];
    return [(h[0] * x + h[1] * y + h[2]) / w, (h[3] * x + h[4] * y + h[5]) / w];
  }
  const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  const line = (a, b) => cross([a[0], a[1], 1], [b[0], b[1], 1]);
  function convex(q) {
    let sign = 0;
    for (let i = 0; i < 4; i++) {
      const [a, b, c] = [q[i], q[(i + 1) % 4], q[(i + 2) % 4]];
      const z = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
      if (Math.abs(z) < 1e-9) return false;
      if (sign && Math.sign(z) !== sign) return false;
      sign = Math.sign(z);
    }
    return true;
  }
  /* First estimate. Image points are normalised around the glass centre; the two vanishing points of the glass edges
     give the vanishing line, which removes the perspective; the paper's two edge directions then fix scale and shear.
     Returns both outlines in mm, glass top-left at the origin and its top edge along x. */
  function rectify(paper, glass, [short, long]) {
    const c = glass.reduce((m, p) => [m[0] + p[0] / 4, m[1] + p[1] / 4], [0, 0]);
    const k = 1 / Math.max(...glass.map(p => dist(p, c)));
    const n = p => [(p[0] - c[0]) * k, (p[1] - c[1]) * k];
    const G = glass.map(n), Q = paper.map(n);
    const l = cross(cross(line(G[0], G[1]), line(G[3], G[2])), cross(line(G[0], G[3]), line(G[1], G[2])));
    if (!(Math.abs(l[2]) > 1e-12)) return null;
    const flat = ([x, y]) => { const w = (l[0] * x + l[1] * y) / l[2] + 1; return [x / w, y / w]; };
    const Gf = G.map(flat), Qf = Q.map(flat);
    const u = [(Qf[1][0] - Qf[0][0] + Qf[2][0] - Qf[3][0]) / 2, (Qf[1][1] - Qf[0][1] + Qf[2][1] - Qf[3][1]) / 2];
    const v = [(Qf[3][0] - Qf[0][0] + Qf[2][0] - Qf[1][0]) / 2, (Qf[3][1] - Qf[0][1] + Qf[2][1] - Qf[1][1]) / 2];
    const det = u[0] * v[1] - u[1] * v[0];
    if (!(Math.abs(det) > 1e-12)) return null;
    const [pw, ph] = Math.hypot(...u) >= Math.hypot(...v) ? [long, short] : [short, long];
    const mm = ([x, y]) => [pw * (v[1] * x - v[0] * y) / det, ph * (u[0] * y - u[1] * x) / det];   // u → (pw, 0), v → (0, ph)
    const g = Gf.map(mm), q = Qf.map(mm);
    const a = -Math.atan2(g[1][1] - g[0][1], g[1][0] - g[0][0]), ca = Math.cos(a), sa = Math.sin(a);
    const frame = ([x, y]) => [ca * (x - g[0][0]) - sa * (y - g[0][1]), sa * (x - g[0][0]) + ca * (y - g[0][1])];
    const gm = g.map(frame), qm = q.map(frame);
    if (gm.concat(qm).some(p => !p.every(Number.isFinite))) return null;
    return { G, Q, k, pw, ph, gm, qm, w: (dist(gm[0], gm[1]) + dist(gm[3], gm[2])) / 2, h: (dist(gm[0], gm[3]) + dist(gm[1], gm[2])) / 2 };
  }
  /* Best estimate: Levenberg-Marquardt over the homography, the glass width and height and the paper's place on the
     glass (13 unknowns), minimising how far the eight dots sit from where that model puts them (16 numbers). */
  function refine(r) {
    const { G, Q, pw, ph } = r, obs = [...G, ...Q];
    const glassAt = (w, h) => [[0, 0], [w, 0], [w, h], [0, h]];
    const paperAt = (tx, ty, t) => [[0, 0], [pw, 0], [pw, ph], [0, ph]].map(([x, y]) =>
      [tx + (Math.cos(t) * x - Math.sin(t) * y) / 1000, ty + (Math.sin(t) * x + Math.cos(t) * y) / 1000]);
    const m = p => p.map(v => v / 1000);
    const h0 = homography([...r.gm.map(m), ...r.qm.map(m)], obs);
    if (!h0) return null;
    let x = [...h0.slice(0, 8), r.w / 1000, r.h / 1000, r.qm[0][0] / 1000, r.qm[0][1] / 1000,
      Math.atan2(r.qm[1][1] - r.qm[0][1], r.qm[1][0] - r.qm[0][0])];
    const residual = x => {
      const h = [...x.slice(0, 8), 1], out = [];
      [...glassAt(x[8], x[9]), ...paperAt(x[10], x[11], x[12])].forEach((p, i) => {
        const q = apply(h, p); out.push(q[0] - obs[i][0], q[1] - obs[i][1]);
      });
      return out;
    };
    const sq = e => e.reduce((s, v) => s + v * v, 0);
    let e = residual(x), cost = sq(e), lambda = 1e-3, JtJ = null;
    for (let it = 0; it < 40; it++) {
      const J = e.map(() => new Array(13));
      for (let j = 0; j < 13; j++) {
        const d = 1e-7 * Math.max(1, Math.abs(x[j])), xp = x.slice(); xp[j] += d;
        const ep = residual(xp);
        for (let i = 0; i < e.length; i++) J[i][j] = (ep[i] - e[i]) / d;
      }
      JtJ = Array.from({ length: 13 }, (_, i) => Array.from({ length: 13 }, (_, j) => J.reduce((s, row) => s + row[i] * row[j], 0)));
      const g = Array.from({ length: 13 }, (_, i) => -J.reduce((s, row, k) => s + row[i] * e[k], 0));
      let stepped = false, small = false;
      for (let t = 0; t < 10 && !stepped; t++) {
        const step = solve(JtJ.map((row, i) => row.map((v, j) => (i === j ? v * (1 + lambda) + 1e-15 : v))), g);
        if (!step) { lambda *= 10; continue; }
        const xn = x.map((v, i) => v + step[i]), en = residual(xn), cn = sq(en);
        if (Number.isFinite(cn) && cn <= cost) {
          small = cost - cn <= 1e-14 * (1 + cost);
          [x, e, cost, stepped] = [xn, en, cn, true];
          lambda = Math.max(1e-12, lambda / 10);
        } else lambda *= 10;
      }
      if (!stepped || small) break;
    }
    if (!x.every(Number.isFinite) || !(x[8] > 0 && x[9] > 0)) return null;
    return { w: x[8] * 1000, h: x[9] * 1000, cost, JtJ };
  }
  /** paper, glass: image points in order top-left, top-right, bottom-right, bottom-left.
      sigmaPx: how precisely a dot can be placed, in image px (sets the error band). */
  function measure(paper, glass, paperKey = 'letter', { sigmaPx = 1.5 } = {}) {
    const size = PAPER_MM[paperKey];
    if (!size || !convex(paper) || !convex(glass)) return { ok: false, reason: 'shape' };
    const first = rectify(paper, glass, size);
    if (!first) return { ok: false, reason: 'shape' };
    const best = refine(first);
    const widthMm = best ? best.w : first.w, heightMm = best ? best.h : first.h;
    if (!(widthMm > 50 && heightMm > 50 && widthMm < 20000 && heightMm < 20000)) return { ok: false, reason: 'range' };
    // error band: 2 sigma of the worse side, from the fit's covariance; sigma = the larger of the expected dot error and
    // what the 3 spare numbers say about these dots (one dot off its corner shows up here)
    let errMm = null, misfitPx = null;
    if (best?.JtJ) {
      misfitPx = Math.sqrt(best.cost / 3) / first.k;
      const s = Math.max(sigmaPx, misfitPx) * first.k;
      const v = j => { const col = new Array(13).fill(0); col[j] = 1; return solve(best.JtJ, col)?.[j]; };
      const vw = v(8), vh = v(9);
      if (vw > 0 && vh > 0) errMm = 2 * s * 1000 * Math.sqrt(Math.max(vw, vh));
    }
    return { ok: true, widthCm: widthMm / 10, heightCm: heightMm / 10, errCm: errMm == null ? null : errMm / 10, misfitPx, method: best ? 'fit' : 'rectify' };
  }
  /* ---------- Finding the paper and the glass; snapping a dot onto the real corner ----------
     Owner 30/10: "kiểu như mấy cái điểm tự gắn vào các góc từ ảnh, tự gắn chính xác vào giấy a4 rồi tới glass corner".
     Plain JS on the device, no library. Images are {w, h, d: grey values, ox, oy (offset of a patch in the photo)}. */
  function greyOf({ width: w, height: h, data }, ox = 0, oy = 0) {
    const d = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) d[i] = 0.299 * data[4 * i] + 0.587 * data[4 * i + 1] + 0.114 * data[4 * i + 2];
    return { w, h, d, ox, oy };
  }
  function sample(g, x, y) {
    x -= g.ox; y -= g.oy;
    if (!(x >= 0 && y >= 0 && x <= g.w - 1 && y <= g.h - 1)) return NaN;
    const x0 = Math.min(g.w - 2, Math.floor(x)), y0 = Math.min(g.h - 2, Math.floor(y)), fx = x - x0, fy = y - y0, i = y0 * g.w + x0;
    return (g.d[i] * (1 - fx) + g.d[i + 1] * fx) * (1 - fy) + (g.d[i + g.w] * (1 - fx) + g.d[i + g.w + 1] * fx) * fy;
  }
  function lineFit(pts) {
    const n = pts.length, m = [pts.reduce((s, q) => s + q[0], 0) / n, pts.reduce((s, q) => s + q[1], 0) / n];
    let xx = 0, xy = 0, yy = 0;
    for (const q of pts) { const dx = q[0] - m[0], dy = q[1] - m[1]; xx += dx * dx; xy += dx * dy; yy += dy * dy; }
    const a = 0.5 * Math.atan2(2 * xy, xx - yy), u = [Math.cos(a), Math.sin(a)], nv = [-u[1], u[0]];
    const off = q => (q[0] - m[0]) * nv[0] + (q[1] - m[1]) * nv[1];
    return { m, u, off, rms: Math.sqrt(pts.reduce((s, q) => s + off(q) ** 2, 0) / n) };
  }
  /** the straight edge leaving corner p along dir (unit vector) within r: profiles across it, one step per profile at
      sub-pixel precision, then a line through those points with stray ones dropped.
      inward: +1 / -1, which side of the edge is inside the shape. pick 'near': the clear step closest to p (the paper,
      lighter or darker than the glass behind it: a sheet against a bright window reads grey); pick 'inner': the innermost
      clear step (the glass edge, not the gasket or frame). */
  function edgeLine(g, p, dir, r, reach, inward, pick) {
    const n = [-dir[1], dir[0]], half = Math.max(3, Math.round(0.7 * r)), found = [];
    for (let t = 0.3 * r; t <= Math.min(1.6 * r, 0.45 * reach); t += Math.max(1, r / 12)) {
      const v = [];
      for (let k = -half - 1; k <= half + 1; k++) v.push(sample(g, p[0] + t * dir[0] + k * n[0], p[1] + t * dir[1] + k * n[1]));
      const step = v.map((_, k) => (k && k < v.length - 1 ? Math.abs(v[k + 1] - v[k - 1]) / 2 : NaN));
      const top = Math.max(0, ...step.filter(Number.isFinite));
      let best = -1;
      for (let k = 2; k < v.length - 2; k++) {
        if (!(step[k] >= (pick === 'inner' ? 0.2 : 0.4) * top && step[k] > 0 && step[k] >= step[k - 1] && step[k] >= step[k + 1])) continue;
        const s = k - half - 1, b = best - half - 1;
        if (best < 0 || (pick === 'near' ? Math.abs(s) < Math.abs(b) : s * inward > b * inward)) best = k;
      }
      if (best < 2 || best > v.length - 3) continue;
      const [a, b, c] = [step[best - 1], step[best], step[best + 1]], den = a - 2 * b + c;
      if (![a, b, c].every(Number.isFinite)) continue;
      const s = best - half - 1 + (den < 0 ? 0.5 * (a - c) / den : 0);
      found.push({ q: [p[0] + t * dir[0] + s * n[0], p[1] + t * dir[1] + s * n[1]], w: b });
    }
    const top = Math.max(0, ...found.map(f => f.w));
    const pts = found.filter(f => f.w >= Math.max(6, 0.35 * top)).map(f => f.q);
    if (pts.length < 6) return null;
    // the line most points sit on (every pair tried: texture and a nearby gasket only add strays), then least squares on those
    let on = [];
    for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
      if (dist(pts[i], pts[j]) < 0.3 * r) continue;
      const L = lineFit([pts[i], pts[j]]), hit = pts.filter(q => Math.abs(L.off(q)) <= 1.5);
      if (hit.length > on.length) on = hit;
    }
    if (on.length < Math.max(6, 0.4 * found.length)) return null;
    const fit = lineFit(on);
    if (fit.rms > 1.2) return null;
    return Math.abs(fit.u[0] * dir[0] + fit.u[1] * dir[1]) >= Math.cos(35 * Math.PI / 180) ? fit : null;
  }
  /** move p onto the corner where the two real edges towards its neighbours qA and qB meet; null when no clear corner.
      pick: 'near' for the paper, 'inner' for the glass (see edgeLine). */
  function snapCorner(g, p, qA, qB, r, pick = 'near') {
    const arm = q => { const l = dist(q, p); return l > 1e-9 ? { d: [(q[0] - p[0]) / l, (q[1] - p[1]) / l], l } : null; };
    const a = arm(qA), b = arm(qB);
    if (!a || !b) return null;
    const side = (u, o) => (-u[1] * o[0] + u[0] * o[1] >= 0 ? 1 : -1);     // the inside lies towards the other arm
    const A = edgeLine(g, p, a.d, r, a.l, side(a.d, b.d), pick), B = edgeLine(g, p, b.d, r, b.l, side(b.d, a.d), pick);
    if (!A || !B) return null;
    const cr = A.u[0] * B.u[1] - A.u[1] * B.u[0];
    if (Math.abs(cr) < Math.sin(25 * Math.PI / 180)) return null;
    const k = ((B.m[0] - A.m[0]) * B.u[1] - (B.m[1] - A.m[1]) * B.u[0]) / cr, c = [A.m[0] + k * A.u[0], A.m[1] + k * A.u[1]];
    return dist(c, p) <= 1.2 * r ? c : null;
  }
  const area = q => Math.abs(q.reduce((s, p, i) => { const n = q[(i + 1) % q.length]; return s + p[0] * n[1] - n[0] * p[1]; }, 0)) / 2;
  /** top-left, top-right, bottom-right, bottom-left */
  function order(q) {
    const c = q.reduce((m, p) => [m[0] + p[0] / 4, m[1] + p[1] / 4], [0, 0]);
    const s = [...q].sort((a, b) => Math.atan2(a[1] - c[1], a[0] - c[0]) - Math.atan2(b[1] - c[1], b[0] - c[0]));
    const k = s.reduce((bi, p, i) => (p[0] + p[1] < s[bi][0] + s[bi][1] ? i : bi), 0);
    return [0, 1, 2, 3].map(i => s[(k + i) % 4]);
  }
  function hull(pts) {
    const p = [...pts].sort((a, b) => a[0] - b[0] || a[1] - b[1]), z = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    const lo = [], up = [];
    for (const q of p) { while (lo.length > 1 && z(lo[lo.length - 2], lo[lo.length - 1], q) <= 0) lo.pop(); lo.push(q); }
    for (const q of p.reverse()) { while (up.length > 1 && z(up[up.length - 2], up[up.length - 1], q) <= 0) up.pop(); up.push(q); }
    return lo.slice(0, -1).concat(up.slice(0, -1));
  }
  function quadOf(h) {
    if (h.length < 4) return null;
    const c = h.reduce((m, q) => [m[0] + q[0] / h.length, m[1] + q[1] / h.length], [0, 0]);
    const far = from => h.reduce((b, q) => (dist(q, from) > dist(b, from) ? q : b), h[0]);
    const a = far(c), b = far(a), side = q => (b[0] - a[0]) * (q[1] - a[1]) - (b[1] - a[1]) * (q[0] - a[0]);
    const pos = h.filter(q => side(q) > 0), neg = h.filter(q => side(q) < 0);
    if (!pos.length || !neg.length) return null;
    return order([a, pos.reduce((m, q) => (side(q) > side(m) ? q : m)), b, neg.reduce((m, q) => (side(q) < side(m) ? q : m))]);
  }
  /** sheets of paper, likeliest first: colourless regions that fill a four-sided shape with paper-like proportions, either
      brighter than what is around them or one flat grey (a sheet against a bright window reads grey) */
  function detectPaper({ width: w, height: h, data }, max = 8) {
    const N = w * h, v = new Uint8Array(N), pale = new Uint8Array(N), hist = new Uint32Array(256);
    for (let i = 0; i < N; i++) {
      const r = data[4 * i], g = data[4 * i + 1], b = data[4 * i + 2], mx = Math.max(r, g, b), mn = Math.min(r, g, b);
      v[i] = mx; pale[i] = mx > 0 && (mx - mn) / mx < 0.22 ? 1 : 0; hist[mx]++;
    }
    const pct = q => { let c = 0; for (let k = 0; k < 256; k++) { c += hist[k]; if (c >= q * N) return k; } return 255; };
    const sm = Array.from(hist, (_, k) => { let s = 0; for (let d = -4; d <= 4; d++) s += hist[Math.min(255, Math.max(0, k + d))]; return s; });
    const modes = sm.map((c, k) => k).filter(k => k >= 60 && sm[k] >= 0.018 * N && sm[k] >= sm[Math.max(0, k - 1)] && sm[k] > sm[Math.min(255, k + 1)])
      .sort((a, b) => sm[b] - sm[a]).slice(0, 8);
    const masks = [
      ...[...new Set([0.6, 0.75, 0.85, 0.92, 0.97].map(q => Math.max(110, pct(q))))].map(T => j => v[j] >= T),
      ...modes.map(T => j => Math.abs(v[j] - T) <= 14),
    ];
    const label = new Int32Array(N), stack = new Int32Array(N), found = [];
    let id = 0;
    for (const inMask of masks) {
      const start = id;                        // labels from an earlier mask count as unvisited
      for (let i0 = 0; i0 < N; i0++) {
        if (label[i0] > start || !pale[i0] || !inMask(i0)) continue;
        const me = ++id, rim = [];
        let top = 0, size = 0, border = false;
        stack[top++] = i0; label[i0] = me;
        while (top) {
          const i = stack[--top], x = i % w, y = (i - x) / w;
          size++;
          if (!x || !y || x === w - 1 || y === h - 1) border = true;
          let edge = false;
          for (const [j, ok] of [[i - 1, x > 0], [i + 1, x < w - 1], [i - w, y > 0], [i + w, y < h - 1]]) {
            if (!ok) continue;
            if (pale[j] && inMask(j)) { if (label[j] !== me) { label[j] = me; stack[top++] = j; } } else edge = true;
          }
          if (edge) rim.push([x + 0.5, y + 0.5]);
        }
        if (border || size < 0.002 * N || size > 0.25 * N) continue;
        const q = quadOf(hull(rim));
        if (!q || !convex(q)) continue;
        const fill = size / area(q);
        const sides = q.map((p, i) => dist(p, q[(i + 1) % 4]));
        const angles = q.map((p, i) => {
          const a = q[(i + 3) % 4], b = q[(i + 1) % 4], u = [a[0] - p[0], a[1] - p[1]], t = [b[0] - p[0], b[1] - p[1]];
          return Math.acos((u[0] * t[0] + u[1] * t[1]) / (Math.hypot(...u) * Math.hypot(...t))) * 180 / Math.PI;
        });
        if (fill < 0.85 || fill > 1.15 || Math.min(...sides) < 0.03 * Math.min(w, h) || angles.some(a => a < 45 || a > 135)) continue;
        const aspect = Math.max(sides[0] + sides[2], sides[1] + sides[3]) / Math.min(sides[0] + sides[2], sides[1] + sides[3]);
        // A4 1.41 and Letter 1.29 stay under 2 even photographed at 45°; a frosted pane (owner photo 30/10: 3.1) does not
        if (aspect > 2) continue;
        // a real sheet has a crisp edge on all four sides; a brightness band on frosted glass or a smudge on a wall does not
        const crisp = q.every((p, i) => {
          const e = q[(i + 1) % 4], len = dist(p, e), u = [(e[0] - p[0]) / len, (e[1] - p[1]) / len], n = [-u[1], u[0]];
          let hit = 0;
          for (let s = 1; s <= 30; s++) {
            const t = s / 31 * len, x = p[0] + u[0] * t, y = p[1] + u[1] * t;
            const at = o => { const xi = Math.round(x + n[0] * o), yi = Math.round(y + n[1] * o); return xi >= 0 && yi >= 0 && xi < w && yi < h ? v[yi * w + xi] : NaN; };
            let step = 0;
            for (let o = -2; o <= 2; o++) step = Math.max(step, Math.abs(at(o + 2) - at(o - 2)) || 0);
            if (step >= 18) hit++;
          }
          return hit >= 18;
        });
        if (!crisp) continue;
        const score = Math.min(fill, 2 - fill) ** 4 * Math.exp(-(((aspect - 1.35) / 0.45) ** 2)) * (1 + 0.1 * Math.log(size));
        found.push({ q, score, c: q.reduce((m, p) => [m[0] + p[0] / 4, m[1] + p[1] / 4], [0, 0]) });
      }
    }
    const out = [];                            // one entry per sheet: the same sheet shows up under several masks
    for (const f of found.sort((a, b) => b.score - a.score)) {
      if (out.some(o => dist(o.c, f.c) < 0.02 * Math.max(w, h))) continue;
      if (out.push(f) >= max) break;
    }
    return out.map(o => o.q);
  }
  /** the glass: four long straight edges around the paper (the inner edge of the frame), chosen by how continuously each
      runs along its whole side; the smaller of two equally clean outlines wins (glass edge, not the outer frame) */
  function detectGlass(g, paper) {
    const { w, h } = g, N = w * h, { gx, gy, mag, thr, fams } = g.lines || (g.lines = straightLines(g));
    const yAt = (L, x) => (L.rho - x * Math.cos(L.th)) / Math.sin(L.th), xAt = (L, y) => (L.rho - y * Math.sin(L.th)) / Math.cos(L.th);
    const m = 0.006 * Math.max(w, h), pc = paper.reduce((s, q) => [s[0] + q[0] / 4, s[1] + q[1] / 4], [0, 0]);
    const side = (lines, test, gap) => {                 // the 6 nearest to the paper and the 3 strongest
      const ok = lines.filter(test);
      return [...new Set([...[...ok].sort((a, b) => gap(a) - gap(b)).slice(0, 6), ...ok.slice(0, 3)])];
    };
    const tops = side(fams[1].lines, L => paper.every(q => yAt(L, q[0]) < q[1] - m), L => pc[1] - yAt(L, pc[0]));
    const bottoms = side(fams[1].lines, L => paper.every(q => yAt(L, q[0]) > q[1] + m), L => yAt(L, pc[0]) - pc[1]);
    const lefts = side(fams[0].lines, L => paper.every(q => xAt(L, q[1]) < q[0] - m), L => pc[0] - xAt(L, pc[1]));
    const rights = side(fams[0].lines, L => paper.every(q => xAt(L, q[1]) > q[0] + m), L => xAt(L, pc[1]) - pc[0]);
    const meet = (H, V) => {
      const a = Math.cos(H.th), b = Math.sin(H.th), c = Math.cos(V.th), e = Math.sin(V.th), det = a * e - b * c;
      return [(H.rho * e - b * V.rho) / det, (a * V.rho - H.rho * c) / det];
    };
    // share of a side that really is an edge facing the right way
    const cover = (p, q) => {
      const len = dist(p, q), u = [(q[0] - p[0]) / len, (q[1] - p[1]) / len], n = [-u[1], u[0]];
      let hit = 0;
      for (let s = 0; s < 60; s++) {
        const t = (s + 0.5) / 60 * len;
        for (let o = -2; o <= 2; o++) {
          const x = Math.round(p[0] + u[0] * t + n[0] * o), y = Math.round(p[1] + u[1] * t + n[1] * o);
          if (x < 1 || y < 1 || x >= w - 1 || y >= h - 1) continue;
          const i = y * w + x;
          if (mag[i] >= thr && Math.abs(gx[i] * n[0] + gy[i] * n[1]) >= 0.9 * mag[i]) { hit++; break; }
        }
      }
      return hit / 60;
    };
    // the most continuous outline; of two equally clean ones the smaller wins (the frame's outer edge loses). The step from
    // gasket to glass, a few px further in, is left to the corner snap ('inner').
    let best = null;
    for (const T of tops) for (const B of bottoms) for (const L of lefts) for (const R of rights) {
      const q = [meet(T, L), meet(T, R), meet(B, R), meet(B, L)];
      if (q.some(p => !(p[0] > -0.01 * w && p[1] > -0.01 * h && p[0] < 1.01 * w && p[1] < 1.01 * h)) || !convex(q)) continue;
      const cov = q.map((p, i) => cover(p, q[(i + 1) % 4]));
      if (Math.min(...cov) < 0.6) continue;
      const score = cov.reduce((s, c) => s + c, 0) / 4 - 0.25 * area(q) / N;
      if (!best || score > best.score) best = { q, score };
    }
    return best ? best.q : null;
  }
  /** edges and the long straight lines of one grey image, worked out once and shared by every paper candidate */
  function straightLines(g) {
    const { w, h, d } = g, N = w * h, gx = new Float32Array(N), gy = new Float32Array(N), mag = new Float32Array(N);
    for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
      const i = y * w + x, a = d[i - w - 1], b = d[i - w], c = d[i - w + 1], l = d[i - 1], r = d[i + 1], e = d[i + w - 1], f = d[i + w], k = d[i + w + 1];
      gx[i] = c + 2 * r + k - a - 2 * l - e; gy[i] = e + 2 * f + k - a - 2 * b - c; mag[i] = Math.hypot(gx[i], gy[i]);
    }
    const samp = []; for (let i = 0; i < N; i += 31) samp.push(mag[i]); samp.sort((x, y) => x - y);
    const thr = Math.max(24, samp[Math.floor(samp.length * 0.85)]);
    const diag = Math.ceil(Math.hypot(w, h)), NR = 2 * diag + 1, BIN = 0.5, SPAN = 35;
    // two families of lines: near-vertical (normal within 35° of 0°) and near-horizontal (normal within 35° of 90°)
    const fams = [{ base: 0, lines: [] }, { base: 90, lines: [] }].map(f => ({ ...f, acc: new Float32Array((2 * SPAN / BIN + 1) * NR) }));
    const NT = 2 * SPAN / BIN + 1, rad = t => t * Math.PI / 180;
    for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      if (mag[i] < thr) continue;
      let th = Math.atan2(gy[i], gx[i]) * 180 / Math.PI;
      if (th > 90) th -= 180; else if (th <= -90) th += 180;
      const f = Math.abs(th) < SPAN ? fams[0] : fams[1];
      const rel = f.base ? (th < 0 ? th + 180 : th) - 90 : th;
      if (Math.abs(rel) >= SPAN) continue;
      for (let dt = -2; dt <= 2; dt += BIN) {
        const t = rel + dt; if (Math.abs(t) > SPAN) continue;
        const ti = Math.round((t + SPAN) / BIN), T = rad(f.base + t), rho = Math.round(x * Math.cos(T) + y * Math.sin(T)) + diag;
        if (rho >= 0 && rho < NR) f.acc[ti * NR + rho] += 1;
      }
    }
    for (const f of fams) {
      const min = 0.12 * (f.base ? w : h), A = f.acc, peaks = [];
      for (let ti = 0; ti < NT; ti++) for (let ri = 0; ri < NR; ri++) {
        const val = A[ti * NR + ri];
        if (val < min) continue;
        let isMax = true;
        for (let a = -6; a <= 6 && isMax; a++) for (let b = -5; b <= 5; b++) {
          const t2 = ti + a, r2 = ri + b;
          if ((a || b) && t2 >= 0 && t2 < NT && r2 >= 0 && r2 < NR && A[t2 * NR + r2] > val) { isMax = false; break; }
        }
        if (isMax) peaks.push({ th: rad(f.base + ti * BIN - SPAN), rho: ri - diag, votes: val });
      }
      f.lines = peaks.sort((a, b) => b.votes - a.votes).slice(0, 40);
    }
    return { gx, gy, mag, thr, fams };
  }
  window.SMART_FILM_MEASURE =Object.freeze({ homography, apply, measure, PAPER_MM, greyOf, snapCorner, detectPaper, detectGlass });

  /* ---------- UI ---------- */
  const $ = id => document.getElementById(id);
  const dialog = $('camera-dialog');
  if (!dialog) return;
  const canvas = $('cam-canvas'), ctx = canvas.getContext('2d');
  const loupe = $('cam-loupe');
  const loupeCanvas = document.createElement('canvas');
  loupe.append(loupeCanvas);
  canvas.tabIndex = 0;
  const steps = [$('cam-step-1'), $('cam-step-2'), $('cam-step-3')];
  const s = { file: null, img: null, view: null, phase: 'paper', drag: null, sel: 0, paper: [], glass: [], result: null, url: null,
    found: { paper: false, glass: false }, touched: { paper: false, glass: false }, scan: null, scans: 0 };
  const COLOUR = { glass: '#e8590c', paper: '#1f5fff' };
  const LOUPE = 132, KNOB = 34;          // css px: magnifier size, how far the round handle sits from its corner

  function show(n) { steps.forEach((step, i) => { step.hidden = i !== n - 1; }); }
  function defaults(w, h, which = ['paper', 'glass']) {
    if (which.includes('paper')) s.paper = [[w * .14, h * .16], [w * .34, h * .16], [w * .34, h * .42], [w * .14, h * .42]];
    if (which.includes('glass')) s.glass = [[w * .08, h * .08], [w * .92, h * .08], [w * .92, h * .92], [w * .08, h * .92]];
  }
  function fit() {
    if (!s.img) return;
    const wrap = canvas.parentElement.getBoundingClientRect();
    const maxW = Math.max(200, wrap.width), maxH = Math.max(240, innerHeight * 0.58);
    const scale = Math.min(maxW / s.img.width, maxH / s.img.height);
    const cssW = Math.round(s.img.width * scale), cssH = Math.round(s.img.height * scale);
    const dpr = Math.min(2, devicePixelRatio || 1);
    canvas.style.width = cssW + 'px'; canvas.style.height = cssH + 'px';
    canvas.width = Math.round(cssW * dpr); canvas.height = Math.round(cssH * dpr);
    s.view = document.createElement('canvas');            // the photo at screen size, drawn once: dragging stays smooth
    s.view.width = canvas.width; s.view.height = canvas.height;
    s.view.getContext('2d').drawImage(s.img, 0, 0, canvas.width, canvas.height);
    draw();
  }
  const cssPerPx = () => canvas.clientWidth / s.img.width;
  /** where the round handle of corner i sits (image px): outward from the shape's centre, inward if that leaves the photo */
  function knob(name, i) {
    const pts = s[name], p = pts[i], k = KNOB / cssPerPx();
    const c = pts.reduce((m, q) => [m[0] + q[0] / 4, m[1] + q[1] / 4], [0, 0]);
    const d = dist(p, c) || 1, dir = [(p[0] - c[0]) / d, (p[1] - c[1]) / d], pad = 14 / cssPerPx();
    const out = [p[0] + dir[0] * k, p[1] + dir[1] * k];
    const inside = out[0] > pad && out[1] > pad && out[0] < s.img.width - pad && out[1] < s.img.height - pad;
    return inside ? out : [p[0] - dir[0] * k, p[1] - dir[1] * k];
  }
  function draw() {
    if (!s.img || !s.view) return;
    const k = canvas.width / s.img.width, u = canvas.width / canvas.clientWidth;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(s.view, 0, 0);
    for (const name of ['glass', 'paper']) {
      const colour = COLOUR[name], active = s.phase === name;
      const pts = s[name].map(([x, y]) => [x * k, y * k]);
      ctx.globalAlpha = active ? 1 : 0.4;
      ctx.lineWidth = 1.5 * u; ctx.strokeStyle = colour; ctx.fillStyle = colour + '1a';
      ctx.beginPath(); pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath(); ctx.fill(); ctx.stroke();
      pts.forEach(([x, y], i) => {
        const r = 9 * u;
        if (active) {
          const [kx, ky] = knob(name, i).map(v => v * k), hot = (s.drag?.index ?? -1) === i;
          stroke(() => { ctx.moveTo(x, y); ctx.lineTo(kx, ky); }, 3 * u, 1.5 * u, colour);
          ctx.beginPath(); ctx.arc(kx, ky, (hot ? 14 : 12) * u, 0, Math.PI * 2);
          ctx.fillStyle = colour; ctx.fill(); ctx.lineWidth = 2.5 * u; ctx.strokeStyle = '#fff'; ctx.stroke();
        }
        // the corner marker: a thin ring and a cross with an open centre, so the corner itself stays visible
        stroke(() => {
          ctx.moveTo(x + r, y); ctx.arc(x, y, r, 0, Math.PI * 2);
          for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { ctx.moveTo(x + dx * 3 * u, y + dy * 3 * u); ctx.lineTo(x + dx * (r + 5 * u), y + dy * (r + 5 * u)); }
        }, 3 * u, 1.25 * u, colour);
      });
      ctx.globalAlpha = 1;
    }
  }
  function stroke(path, under, over, colour, c = ctx) {
    c.beginPath(); path();
    c.lineWidth = under; c.strokeStyle = 'rgba(255,255,255,.85)'; c.stroke();
    c.lineWidth = over; c.strokeStyle = colour; c.stroke();
  }
  function toImage(event) {
    const rect = canvas.getBoundingClientRect();
    return [(event.clientX - rect.left) / rect.width * s.img.width, (event.clientY - rect.top) / rect.height * s.img.height];
  }
  /* Magnifier: the full-resolution photo around the corner, a cross on the exact point and the two edges leaving it, so the
     cross can be laid on the real corner and the lines along the real edges. Sits in a top corner, away from the finger. */
  function showLoupe(i) {
    const pts = s[s.phase], p = pts[i], colour = COLOUR[s.phase];
    const dpr = Math.min(2, devicePixelRatio || 1), px = Math.round(LOUPE * dpr);
    if (loupeCanvas.width !== px) { loupeCanvas.width = loupeCanvas.height = px; loupeCanvas.style.width = loupeCanvas.style.height = LOUPE + 'px'; }
    const span = Math.max(24, 64 * Math.max(s.img.width, s.img.height) / 3000);   // photo px across the lens
    const z = px / span, l = loupeCanvas.getContext('2d');
    l.setTransform(1, 0, 0, 1, 0, 0);
    l.fillStyle = '#111'; l.fillRect(0, 0, px, px);
    const sx = Math.max(0, p[0] - span / 2), sy = Math.max(0, p[1] - span / 2);
    const sw = Math.min(s.img.width, p[0] + span / 2) - sx, sh = Math.min(s.img.height, p[1] + span / 2) - sy;
    if (sw > 0 && sh > 0) l.drawImage(s.img, sx, sy, sw, sh, (sx - (p[0] - span / 2)) * z, (sy - (p[1] - span / 2)) * z, sw * z, sh * z);
    const c = px / 2, at = q => [c + (q[0] - p[0]) * z, c + (q[1] - p[1]) * z];
    for (const j of [(i + 1) % 4, (i + 3) % 4]) stroke(() => { l.moveTo(c, c); l.lineTo(...at(pts[j])); }, 3 * dpr, 1.5 * dpr, colour, l);
    stroke(() => {
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { l.moveTo(c + dx * 5 * dpr, c + dy * 5 * dpr); l.lineTo(c + dx * 26 * dpr, c + dy * 26 * dpr); }
    }, 3.5 * dpr, 1.5 * dpr, '#111', l);
    const wrap = canvas.parentElement.getBoundingClientRect(), rect = canvas.getBoundingClientRect();
    const qx = rect.left - wrap.left + p[0] * cssPerPx(), qy = rect.top - wrap.top + p[1] * cssPerPx();
    const right = qx < LOUPE + 40 && qy < LOUPE + 40;
    loupe.style.left = (right ? wrap.width - LOUPE - 8 : 8) + 'px';
    loupe.style.top = '8px';
    loupe.hidden = false;
  }
  function move(i, dx, dy) {
    const p = s[s.phase][i];
    s[s.phase][i] = [Math.max(0, Math.min(s.img.width, p[0] + dx)), Math.max(0, Math.min(s.img.height, p[1] + dy))];
    draw(); showLoupe(i);
  }
  canvas.addEventListener('pointerdown', event => {
    if (!s.img) return;
    const p = toImage(event), reach = 30 / cssPerPx();
    let best = -1, bestD = Infinity;
    s[s.phase].forEach((q, i) => {
      const d = Math.min(dist(p, q), dist(p, knob(s.phase, i)));
      if (d < bestD) { bestD = d; best = i; }
    });
    if (best < 0 || bestD > reach) return;
    s.sel = best; s.touched[s.phase] = true;
    s.drag = { index: best, id: event.pointerId, x: event.clientX, y: event.clientY, t: event.timeStamp };
    canvas.setPointerCapture(event.pointerId);
    draw(); showLoupe(best);
    event.preventDefault();
  });
  // The point moves with the finger, never jumps to it; a slow finger moves it in finer steps.
  canvas.addEventListener('pointermove', event => {
    const d = s.drag;
    if (!d || event.pointerId !== d.id) return;
    const dx = event.clientX - d.x, dy = event.clientY - d.y, dt = Math.max(1, event.timeStamp - d.t);
    const speed = Math.hypot(dx, dy) / dt, gain = speed < 0.12 ? 0.3 : speed < 0.4 ? 0.6 : 1;
    Object.assign(d, { x: event.clientX, y: event.clientY, t: event.timeStamp });
    move(d.index, dx * gain / cssPerPx(), dy * gain / cssPerPx());
  });
  const snapOn = () => $('cam-snap')?.checked !== false;
  /** snap corner i of the current outline onto the real corner near it (full-resolution photo around it) */
  function snapAt(name, i, fine) {
    // a dropped dot is a finger's width away (16 css px); an auto-found one is already within a few photo px
    const long = Math.max(s.img.width, s.img.height);
    const p = s[name][i], r = fine ? Math.max(8, (name === 'glass' ? 0.015 : 0.008) * long) : Math.max(8, Math.min(0.04 * long, 16 / cssPerPx()));
    const x0 = Math.max(0, Math.floor(p[0] - 2.2 * r)), y0 = Math.max(0, Math.floor(p[1] - 2.2 * r));
    const x1 = Math.min(s.img.width, Math.ceil(p[0] + 2.2 * r)), y1 = Math.min(s.img.height, Math.ceil(p[1] + 2.2 * r));
    if (x1 - x0 < 4 || y1 - y0 < 4) return false;
    const g = greyOf(s.img.getContext('2d', { willReadFrequently: true }).getImageData(x0, y0, x1 - x0, y1 - y0), x0, y0);
    const c = snapCorner(g, p, s[name][(i + 1) % 4], s[name][(i + 3) % 4], r, name === 'paper' ? 'near' : 'inner');
    if (c) s[name][i] = c;
    return !!c;
  }
  /** find the paper (on load) and the glass (around the paper) in a ≤ 1000 px copy, then snap every corner at full size */
  function autoFind(name) {
    if (!s.scan) {
      const k = Math.min(1, 1000 / Math.max(s.img.width, s.img.height)), c = document.createElement('canvas');
      c.width = Math.round(s.img.width * k); c.height = Math.round(s.img.height * k);
      const x = c.getContext('2d', { willReadFrequently: true });
      x.drawImage(s.img, 0, 0, c.width, c.height);
      const img = x.getImageData(0, 0, c.width, c.height);
      s.scan = { k, img, grey: greyOf(img) };
    }
    const { k, img, grey } = s.scan;
    let q = null;
    try {
      if (name === 'paper') {
        // Owner photo 30/10: a frosted pane is bright and colourless too, and a wall switch is sheet-shaped. A candidate counts
        // only if a glass outline is found around it and it covers at most a quarter of that glass; else nothing is guessed.
        for (const c of detectPaper(img)) {
          const g = detectGlass(grey, c);
          if (g && area(c) <= 0.25 * area(g)) { q = c; s.scan.glass = g; break; }
        }
      } else q = s.scan.glass && !s.touched.paper ? s.scan.glass : detectGlass(grey, s.paper.map(p => [p[0] * k, p[1] * k]));
    } catch { q = null; }
    if (!q) return false;
    s[name] = q.map(p => [p[0] / k, p[1] / k]);
    for (let pass = 0; pass < 2; pass++) for (let i = 0; i < 4; i++) snapAt(name, i, true);
    return (s.found[name] = true);
  }
  const endDrag = () => {
    const d = s.drag;
    s.drag = null; loupe.hidden = true;
    if (d && snapOn()) snapAt(s.phase, d.index);
    draw();
  };
  canvas.addEventListener('pointerup', endDrag);
  canvas.addEventListener('pointercancel', endDrag);
  // Keyboard: Tab focuses the photo, 1–4 picks a corner, arrows move it by one photo pixel (Shift: ten).
  canvas.addEventListener('keydown', event => {
    if (!s.img) return;
    if (/^[1-4]$/.test(event.key)) { s.sel = Number(event.key) - 1; showLoupe(s.sel); event.preventDefault(); return; }
    const step = event.shiftKey ? 10 : 1, dirs = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    if (!dirs[event.key]) return;
    move(s.sel, dirs[event.key][0] * step, dirs[event.key][1] * step);
    event.preventDefault();
  });
  canvas.addEventListener('blur', () => { if (!s.drag) loupe.hidden = true; });

  const paperKey = () => dialog.querySelector('input[name="paper"]:checked')?.value || 'letter';
  function setPhase(phase) {
    s.phase = phase; s.sel = 0;
    // the glass is looked for once the paper is set, unless the shopper already moved the orange dots
    if (phase === 'glass' && !s.found.glass && !s.touched.glass && s.img) autoFind('glass');
    const paper = PAPER_NAME[paperKey()], found = s.found[phase];
    $('cam-instruct').innerHTML = phase === 'paper'
      ? (found
        ? `<b>Step 1 of 2:</b> we found the ${paper} sheet. Check that each <span class="${P}tag ${P}tag-paper">blue</span> cross sits exactly on a corner of the paper.`
        : `<b>Step 1 of 2: we couldn't find the paper.</b> Drag the <span class="${P}tag ${P}tag-paper">blue</span> dots onto the 4 corners of your sheet.`)
        + ' A magnifier shows while you drag, and a dot you let go of clicks onto the nearest corner.'
      : `<b>Step 2 of 2:</b> ${found ? 'we found the glass. Check that' : 'drag the handles until'} each <span class="${P}tag ${P}tag-glass">orange</span> cross sits exactly on a corner of the glass, inside the frame.`;
    $('cam-next').textContent = phase === 'paper' ? 'Next' : 'Measure';
    draw();
  }
  function unitText(cm) {
    const unit = $('unit-cm')?.getAttribute('aria-pressed') === 'true' ? 'cm' : 'in';
    if (unit === 'cm') return `${(Math.round(cm * 10) / 10).toFixed(1)} cm`;
    const eighths = Math.round(cm / 2.54 * 8);
    const whole = Math.floor(eighths / 8), rest = eighths % 8;
    const frac = ['', '⅛', '¼', '⅜', '½', '⅝', '¾', '⅞'][rest];
    return `${whole || !frac ? whole : ''}${whole && frac ? ' ' : ''}${frac} in`;
  }
  async function load(file) {
    if (!file) return;
    const fail = text => { $('cam-error').textContent = text; $('cam-error').hidden = false; };
    $('cam-error').hidden = true;
    if (file.size > 25 * 1024 * 1024) { fail('Please choose a photo under 25 MB.'); return; }
    if (s.url) URL.revokeObjectURL(s.url);
    s.url = URL.createObjectURL(file);
    const img = new Image();
    img.src = s.url;
    try { await img.decode(); } catch { fail('This photo could not be opened. Try another one.'); return; }
    // Work on a copy up to 3000 px (12 MP cap keeps iPhone canvas memory safe): the magnifier needs the detail.
    const k = Math.min(1, 3000 / Math.max(img.naturalWidth, img.naturalHeight), Math.sqrt(12e6 / (img.naturalWidth * img.naturalHeight)));
    const off = document.createElement('canvas');
    off.width = Math.round(img.naturalWidth * k); off.height = Math.round(img.naturalHeight * k);
    off.getContext('2d').drawImage(img, 0, 0, off.width, off.height);
    s.img = off; s.file = file; s.view = null; s.scan = null;
    s.found = { paper: false, glass: false }; s.touched = { paper: false, glass: false };
    defaults(off.width, off.height);
    show(2); setPhase('paper');
    $('cam-instruct').innerHTML = '<b>Finding the paper and the glass…</b>';
    requestAnimationFrame(() => {
      fit();
      setTimeout(() => {
        autoFind('paper');
        if (s.found.paper) autoFind('glass');
        setPhase('paper'); s.scans = (s.scans || 0) + 1;
      }, 30);
    });
  }
  // Owner 30/10: on iPhone "capture" opens the camera only, so the library gets its own button (cam-lib, no capture).
  // A mouse-and-keyboard computer has no camera to open: only the picker shows there, as the main button.
  for (const id of ['cam-file', 'cam-lib']) $(id)?.addEventListener('change', () => load($(id).files?.[0]));
  if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
    dialog.querySelector(`.${P}cam-pick-take`)?.setAttribute('hidden', '');
    dialog.querySelector(`.${P}cam-pick-lib`)?.classList.replace(`${P}btn-ghost`, `${P}btn-primary`);
  }
  $('cam-back').addEventListener('click', () => { if (s.phase === 'glass') setPhase('paper'); else show(1); });
  $('cam-next').addEventListener('click', () => {
    // dots that were neither found nor moved are only a placeholder: nothing is measured from them
    if (!s.found[s.phase] && !s.touched[s.phase]) {
      $('cam-instruct').innerHTML = s.phase === 'paper'
        ? `<b>Place the <span class="${P}tag ${P}tag-paper">blue</span> dots first:</b> drag them onto the 4 corners of your sheet of printer paper.`
        : `<b>Place the <span class="${P}tag ${P}tag-glass">orange</span> dots first:</b> drag them onto the 4 corners of the glass.`;
      return;
    }
    if (s.phase === 'paper') { setPhase('glass'); return; }
    const key = paperKey();
    // a dot can be placed to about 0.07 % of the photo's long side with the magnifier (≈ 2 px on a 3000 px photo)
    const result = measure(s.paper, s.glass, key, { sigmaPx: 0.0007 * Math.max(s.img.width, s.img.height) });
    if (!result.ok) {
      $('cam-instruct').innerHTML = '<b>Those points don\'t make a rectangle.</b> Check each cross sits on a corner, in order around the shape.';
      return;
    }
    // a wrong size means wrongly cut film: wider than ± 5 cm (or no error band) is not shown as a size at all
    const unusable = result.errCm == null || result.errCm > 5;
    s.result = unusable ? null : result;
    const band = result.errCm == null ? '' : ` (about ± ${unitText(Math.max(0.3, result.errCm))})`;
    const paper = `Measured against ${PAPER_NAME[key]} paper (${key === 'a4' ? '210 × 297 mm' : '8.5 × 11 in'}).`;
    steps[2].querySelector('h2').textContent = unusable ? 'We couldn\'t measure this photo' : 'Your glass measures about';
    $('cam-result').textContent = unusable ? (result.errCm == null ? 'Too rough' : `± ${unitText(result.errCm)}`) : `${unitText(result.widthCm)} × ${unitText(result.heightCm)}`;
    $('cam-quality').textContent = unusable
      ? 'That is too far off to cut film from. Retake the photo closer and straighter, with one sheet of printer paper taped flat on the glass and fully in view, then check every cross with the magnifier. Or measure with a tape measure.'
      : result.errCm > 3
        ? `Rough estimate${band}. ${paper} For a closer number, check each cross with the magnifier, or take the photo closer and straighter.`
        : `Good estimate${band}. ${paper}`;
    $('cam-use').disabled = unusable;
    show(3);
    (unusable ? $('cam-redo') : $('cam-use')).focus();
  });
  $('cam-redo').addEventListener('click', () => { show(2); setPhase('glass'); requestAnimationFrame(fit); });
  $('cam-use').addEventListener('click', async () => {
    const app = window.SMART_FILM_APP;
    if (!s.result || !app?.setMeasurementsCm) return;
    app.setMeasurementsCm(s.result.widthCm, s.result.heightCm, 'camera');
    if (s.file && app.usePhoto) await app.usePhoto(s.file, 'camera');
    app.closeDialog?.(dialog, false);
    document.getElementById('quote-price')?.closest(`.${P}your-price`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
  addEventListener('resize', () => { if (!steps[1].hidden) fit(); });
  // read-only view of the dots for the automated checks (scripts/qa)
  window.SMART_FILM_MEASURE_UI = Object.freeze({ points: () => ({ paper: s.paper.map(p => [...p]), glass: s.glass.map(p => [...p]), found: { ...s.found }, scans: s.scans || 0 }) });
  for (const id of ['open-camera-measure', 'open-camera-measure-2']) {
    $(id)?.addEventListener('click', () => {
      show(1);
      $('cam-file').value = '';
      if ($('cam-lib')) $('cam-lib').value = '';
      const app = window.SMART_FILM_APP;
      if (app?.openDialog) app.openDialog(dialog, $(id)); else dialog.showModal();
    });
  }
})();
